import { FilterParams } from "@/api/adapter.api";
import { State } from "@/store";
import { Store } from "vuex";
import { parseUrlQuery } from "./url-query.helper";
import { Route } from "vue-router";
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import { PlaceCategoryArray } from "./filter-genre.const";
import useFilterGenreUsecase from "@/usecase/use-filter-genre.usecase";
import { CategoryMapType } from "@/api/types/genre";

/** @remarks マップタイプ
 * alpha版からジャンル名とタイプ名は同じにDBに設定されたから利用し続く
 * normal: 日常マップ (DBに記録してない)
 * polling-place: 投票所マップ
 * evacuation-center: 避難所マップ（防災）
 * water-gate: 水門マップ
 */
export type MapsMapType = "normal" | "polling-place" | "evacuation-center" | "water-gate";

export function genreToMapsMapType(genres: any[]): MapsMapType {
  return genres.includes("evacuation-center")
    ? "evacuation-center"
    : genres.includes("disaster-prevention")
    ? "water-gate"
    : genres.includes("polling-place")
    ? "polling-place"
    : "normal";
}

// Genres that double as a non-normal map mode marker. A selectedGenres list only
// re-derives mapsMapType when it contains one of these (see data-module.ts's
// setSelectedGenres mutation) — a generic genre like "restaurant" or a related but
// non-marker genre like "water-station" never changes the current map mode.
export const MAP_TYPE_MARKER_GENRES: string[] = ["polling-place", "evacuation-center", "disaster-prevention"];

export function hasMapTypeMarkerGenre(genres: any[]): boolean {
  return genres.some((genre) => MAP_TYPE_MARKER_GENRES.includes(genre));
}

// Only specfic 4 mapTypes can be remap
export function mapTypeToGenre(mapType: MapsMapType): Partial<PortalAdapterApi.PlaceCategoryType | "all">[] {
  return mapType === "normal"
    ? []
    : mapType === "water-gate"
    ? ["disaster-prevention"]
    : mapType === "polling-place"
    ? ["polling-place"]
    : mapType === "evacuation-center"
    ? ["evacuation-center"]
    : [];
}

export function getFilterParamsFromUrlQuery(queryParam: Route["query"]): FilterParams {
  const {
    vacant,
    favorite,
    genre,
    genreCategoryList,
    mapsMapType,
  }: {
    vacant?: string;
    favorite?: string;
    genre?: string;
    genreCategoryList?: string;
    mapsMapType?: string;
  } = queryParam;

  const query: Omit<FilterParams, "isOpendata"> = {
    vacant,
    favorite,
    genre: parseUrlQuery(genre) ?? [],
    genreCategoryList: parseUrlQuery(genreCategoryList) ?? [],
    mapsMapType: mapsMapType || undefined,
  };
  return query;
}

export function cleanFilterParamsStore(typedStore: Store<State>, mapType: MapsMapType) {
  typedStore.commit("data/setSelectedFilters", []);

  const genre = [mapType === "normal" ? "" : mapType];
  typedStore.commit("data/setSelectedGenres", genre);

  typedStore.commit("data/setSelectedGenreCategoryList", []);
}

export function getQueryFilterParamsFromStore(typedStore: Store<State>): FilterParams {
  const mapType = typedStore.state.data.mapsMapType;
  return {
    vacant: typedStore.state.data.selectedFilters.find((radio: string) => radio === "vacant") ? "1" : undefined,
    favorite: typedStore.state.data.selectedFilters.find((radio: string) => radio === "favorite") ? "1" : undefined,
    genre: typedStore.state.data.selectedGenres,
    genreCategoryList: typedStore.state.data.selectedGenreCategoryList,
    isOpendata: typedStore.state.data.isOpendata,
    // Carry the map mode in the URL. Left undefined on the normal map so that,
    // when switching back to normal, it overrides (and drops) any stale value.
    mapsMapType: mapType === "normal" ? undefined : mapType,
  };
}

export function validatedAllGenres(genres?: string[]): (PortalAdapterApi.PlaceCategoryType | "all")[] {
  return (genres ?? []).filter((genre): genre is PortalAdapterApi.PlaceCategoryType | "all" => {
    return [...PlaceCategoryArray, "all"].includes(genre as PortalAdapterApi.PlaceCategoryType | "all");
  });
}

/**
 * Decide the `genre` query param sent to /v1/places.
 *
 * On a special map (evacuation-center / polling-place / water-gate) the map mode
 * is already carried by `mapsMapType`, so the leading genre value is only a mode
 * marker rather than a real filter. We therefore only send `genre` when the user
 * has narrowed the result with a specific genre or category selection; otherwise
 * we omit it so the backend returns *all* genres for that map type.
 *
 * On the normal map the genre list is passed through unchanged.
 */
export async function resolveFetchGenre({
  mapsMapType,
  genre,
  genreCategoryList,
  countryName = "jp",
}: {
  mapsMapType: string | undefined;
  genre: string[];
  genreCategoryList?: string[];
  countryName?: string;
}): Promise<string[]> {
  console.log(`----resolveFetchGenre----`);
  console.log(mapsMapType, genre, genreCategoryList, countryName);
  if (!mapsMapType || mapsMapType === "normal") return genre; // passed through unchanged
  else if (genre.length === 0) {
    if (mapsMapType === "evacuation-center") {
      // UI allows single selection in final draft, so
      // ["evacuation-center"] — single default, no API call needed
      return mapTypeToGenre(mapsMapType);
    }
    // TODO: if UI changed to multiple selection in future
    // take off above to unify the functionality
    const filterGenreUsecase = useFilterGenreUsecase();
    const _genres = await filterGenreUsecase.fetchGenreList(countryName, mapsMapType);
    return _genres.map((item) => item.placeCategoryType);
  }
  return genre;
}

export function allSameMapType(items: CategoryMapType[]): boolean {
  return new Set(items.map((item) => item.mapType)).size <= 1;
}
